import { Grid, Tab, Tabs } from "@mui/material";
import { useMemo, useState } from "react";
import { PersistedTabPanel } from "../../../../../../../../common/PersistedTabPanel";
import type { DrillDownPage } from "../../../../../store/types/drillDownPageState.types";
import ChartOfAccountsPart from "../chartOfAccounts/ChartOfAccountsPart";
import { drillDownTabSx, drillDownTabsSx } from "../constants";
import { isMeasureDrillDownInfo } from "../isMeasureDrillDownInfo";
import LedgerPart from "../ledger/LedgerPart";
import { getLedgerTitle } from "../utils/getLedgerTitle";
import CashFlowPart from "./CashFlowPart";
import type { DrillDownInfo, IrrDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";

enum IrrPageTab {
  CashFlow = "cashflow",
  ChartOfAccounts = "chartOfAccounts",
  Ledger = "ledger",
}

interface Props {
  configuration: DrillDownPage;
  drillDownInfo: DrillDownInfo;
}

export default function IrrPage({ configuration, drillDownInfo }: Props) {
  const [selectedTab, setSelectedTab] = useState<IrrPageTab>(IrrPageTab.CashFlow);
  const combined = drillDownInfo.combined as IrrDrillDownInfo;
  const ledgers = useMemo(
    () => drillDownInfo.items.filter(isMeasureDrillDownInfo).map(getLedgerTitle),
    [drillDownInfo.items]
  );
  const measureItems = useMemo(() => drillDownInfo.items.filter(isMeasureDrillDownInfo), [drillDownInfo.items]);

  return (
    <>
      <Tabs value={selectedTab} sx={drillDownTabsSx} onChange={(_, value: IrrPageTab) => setSelectedTab(value)}>
        <Tab key={IrrPageTab.CashFlow} label="Cash Flow" value={IrrPageTab.CashFlow} sx={drillDownTabSx} />
        <Tab
          key={IrrPageTab.ChartOfAccounts}
          label="Chart of Accounts"
          value={IrrPageTab.ChartOfAccounts}
          sx={drillDownTabSx}
        />
        {ledgers.map((ledgerName, index) => (
          <Tab
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`${IrrPageTab.Ledger}_${index}`}
            label={ledgerName}
            value={`${IrrPageTab.Ledger}_${index}`}
            sx={drillDownTabSx}
          />
        ))}
      </Tabs>
      <Grid container sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <PersistedTabPanel
          key={IrrPageTab.CashFlow}
          value={IrrPageTab.CashFlow}
          selectedTab={selectedTab}
          sx={{
            height: "100%",
            minHeight: 0,
          }}
        >
          <CashFlowPart configuration={configuration} info={combined} />
        </PersistedTabPanel>
        <PersistedTabPanel
          key={IrrPageTab.ChartOfAccounts}
          value={IrrPageTab.ChartOfAccounts}
          selectedTab={selectedTab}
          sx={{
            height: "100%",
            minHeight: 0,
          }}
        >
          <ChartOfAccountsPart configuration={configuration} info={drillDownInfo} />
        </PersistedTabPanel>
        {measureItems.map((item, index) => (
          <PersistedTabPanel
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`${IrrPageTab.Ledger}_${index}`}
            value={`${IrrPageTab.Ledger}_${index}`}
            selectedTab={selectedTab}
            sx={{
              height: "100%",
              minHeight: 0,
            }}
          >
            <LedgerPart configuration={configuration} info={item} />
          </PersistedTabPanel>
        ))}
      </Grid>
    </>
  );
}
